/**
 * Shared display formatters for the operations books and the dashboard /
 * Firm Success rosters.
 *
 * Each of these was copy-pasted across the route and panel files. They are
 * consolidated here verbatim; where two copies genuinely differed (the date
 * year), the difference is preserved as an explicit option rather than
 * silently normalised.
 *
 * NOTE: case-detail-panel.tsx keeps its OWN fmtDate/fmtDateYear. They take
 * their day from `dayZone` below, like fmtDate, and differ from it in two
 * ways: they format in the `en-US` locale and fall back to the raw `iso` on
 * an unparseable input, where fmtDate uses the ambient locale and an
 * empty-string fallback. Folding them together would change rendered output,
 * so they stay separate.
 */

/** A date-only `YYYY-MM-DD` — the value an Airtable date field carries. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The instant to format and the zone to format it in, so a value shows the
 * same calendar day to every viewer. A date-only `YYYY-MM-DD` is its own day:
 * it is anchored at UTC midnight and formatted in UTC (#785). A full ISO
 * instant belongs to the Pacific day containing it (engineering/contract/
 * dimensions.ts, `day`; `dateInPT` in the worker), so it is formatted in
 * America/Los_Angeles. Null for an unparseable input.
 */
export function dayZone(iso: string): { ms: number; timeZone: string } | null {
  const dateOnly = DATE_ONLY.test(iso);
  const ms = Date.parse(dateOnly ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(ms)) return null;
  return { ms, timeZone: dateOnly ? "UTC" : "America/Los_Angeles" };
}

/**
 * Compact absolute date. Without a year — "Apr 27"; with `{ year: true }` —
 * "Apr 27, 2026". The day comes from `dayZone`: a date-only value is its own
 * day, an instant is its Pacific day, for every viewer. Renders in the
 * ambient locale; an unparseable input renders as the empty string. Shared
 * by the operations book surfaces (under-review / accepted / settled render
 * bare; dropped / placed / rejected / closed render with the year, which
 * is load-bearing because those books span back a year or more).
 */
export function fmtDate(iso: string, opts?: { year?: boolean }): string {
  const z = dayZone(iso);
  if (z === null) return "";
  return new Date(z.ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(opts?.year ? { year: "numeric" } : {}),
    timeZone: z.timeZone,
  });
}

/** Compact whole-dollar settlement amount — "$125,000". */
export function fmtCurrency(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Compact time-in-book label that scales with magnitude: days under a
 * couple months, months under a year, then years to one decimal.
 */
export function fmtDuration(days: number): string {
  if (days < 1) return "<1d";
  if (days < 60) return `${Math.round(days)}d`;
  if (days < 365) return `${Math.round(days / 30.4)}mo`;
  return `${(days / 365).toFixed(1)}y`;
}

/** Whole-day age label. Sub-day reads "<1d" rather than "0d". */
export function fmtDays(days: number): string {
  return days < 1 ? "<1d" : `${Math.round(days)}d`;
}

/**
 * First letters of a name, max 2, uppercased. Used as the avatar / logo
 * fallback glyph ("Manuel Solis" → "MS"). An empty name reads "?".
 */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Relative day label vs `today` (both YYYY-MM-DD, compared in UTC):
 * "today" / "tomorrow" / "yesterday", otherwise the compact absolute date
 * ("Jun 25"). Dashboard date chips (ADR-0104) — "Due tomorrow" beats
 * "Due 2026-06-12" at a glance.
 */
export function fmtRelativeDay(ymd: string, today: string): string {
  const target = Date.parse(`${ymd}T00:00:00Z`);
  const base = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(target) || Number.isNaN(base)) return "";
  const diff = Math.round((target - base) / 86_400_000);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";
  // Format the calendar day AS WRITTEN: construct local midnight from the
  // parts. Routing the YMD through fmtDate (which parses as UTC and renders
  // local) would shift it back a day for operators west of UTC — the
  // parking-lot #1 class of timezone bug.
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/**
 * Compact "how long ago" for an ISO datetime: "just now" under a minute,
 * then "Nm ago" / "Nh ago" / "Nd ago". `nowMs` injectable for tests.
 * Future or unparseable timestamps read "just now" (clock-skew defensive).
 */
export function fmtTimeAgo(iso: string, nowMs: number = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "just now";
  const mins = Math.floor((nowMs - then) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Routing status → badge variant. Active emerald; Paused amber. */
export function routingVariant(s: string | null): "emerald" | "amber" | "muted" {
  switch (s) {
    case "Active":
      return "emerald";
    case "Paused":
      return "amber";
    default:
      return "muted";
  }
}
